import React from "react";
import { AssayDriftOemList } from "./AssayDriftView";

const FIELD_ORDER = ["subweight", "ptAssay", "pdAssay", "rhAssay"];

const BUCKET_STYLES = {
  "0-5": { bg: "#d4edda", color: "#1e6b3a", label: "Low (0–5%)" },
  "5-20": { bg: "#fff3cd", color: "#856404", label: "Moderate (5–20%)" },
  "20-50": { bg: "#ffe0b2", color: "#c25e00", label: "High (20–50%)" },
  above50: { bg: "#f8d7da", color: "#9b1c2e", label: "Very high (50%+)" },
  empty: { bg: "#f0f0f0", color: "#999", label: "No data" },
};

function getCellStyle(cell) {
  if (!cell || (cell.testedCount ?? 0) === 0) {
    return BUCKET_STYLES.empty;
  }
  return BUCKET_STYLES[cell.bucketId] || BUCKET_STYLES["5-20"];
}

export function AssayHeatMapGrid({ summary, selectedType, selectedField, onSelect, onClear }) {
  const heatmap = summary?.heatmap || [];
  const fields = summary?.fields || [];

  const fieldOrder = FIELD_ORDER.map((fieldId) => {
    const meta = fields.find((f) => f.field === fieldId);
    return meta || { field: fieldId, label: fieldId };
  });

  if (!heatmap.length) {
    return <div className="c-gray fs-14">No assay heat map data available.</div>;
  }

  return (
    <div className="converter-testing-heatmap">
      <div className="converter-testing-executive-title">Assay drift heat map</div>
      <p className="converter-testing-drift-help c-gray fs-13 mb-16">
        Average drift % by converter type and assay field. Color shows severity (green = low, red = very high).
        Click a cell to list OEMs for that type and field.
      </p>

      <div className="converter-testing-heatmap-legend">
        {Object.entries(BUCKET_STYLES)
          .filter(([key]) => key !== "empty")
          .map(([key, style]) => (
            <div key={key} className="converter-testing-heatmap-legend-item">
              <span className="converter-testing-heatmap-swatch" style={{ background: style.bg }} />
              <span className="fs-12">{style.label}</span>
            </div>
          ))}
      </div>

      <div className="converter-testing-heatmap-wrap">
        <table className="converter-testing-heatmap-table">
          <thead>
            <tr>
              <th>Type</th>
              {fieldOrder.map((field) => (
                <th key={field.field}>{field.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {heatmap.map((row) => (
              <tr key={row.type}>
                <th scope="row" className="converter-testing-heatmap-type-label">
                  {row.type}
                </th>
                {fieldOrder.map((field) => {
                  const cell = row.cells?.[field.field];
                  const style = getCellStyle(cell);
                  const isActive = selectedType === row.type && selectedField === field.field;
                  const testedCount = cell?.testedCount ?? 0;
                  return (
                    <td key={`${row.type}-${field.field}`} className="converter-testing-heatmap-cell-td">
                      <button
                        type="button"
                        className={`converter-testing-heatmap-cell ${isActive ? "active" : ""}`}
                        style={{ background: style.bg, color: style.color }}
                        disabled={testedCount === 0}
                        onClick={() => onSelect(row.type, field.field)}
                        title={
                          testedCount === 0
                            ? "No tested converters"
                            : `Avg drift ${cell.avgDriftPct}% · ${testedCount} tested · ${cell.highDriftCount ?? 0} above 20%`
                        }
                      >
                        <span className="converter-testing-heatmap-cell-value">
                          {testedCount === 0 ? "—" : `${cell.avgDriftPct}%`}
                        </span>
                        <span className="converter-testing-heatmap-cell-meta">
                          {testedCount === 0 ? "n/a" : `${testedCount} tested`}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedType && selectedField && (
        <button type="button" className="converter-testing-details-toggle" onClick={onClear}>
          ✕ Clear selection ({selectedType})
        </button>
      )}
    </div>
  );
}

export function AssayHeatMapOemList({
  items,
  heatMapType,
  driftField,
  totalCount,
  offset,
  pageSize,
  areMoreItemsAvailable,
  onPrev,
  onNext,
}) {
  if (!heatMapType || !driftField) return null;

  return (
    <AssayDriftOemList
      items={items}
      driftField={driftField}
      contextLabel={heatMapType}
      totalCount={totalCount}
      offset={offset}
      pageSize={pageSize}
      areMoreItemsAvailable={areMoreItemsAvailable}
      onPrev={onPrev}
      onNext={onNext}
    />
  );
}
